/**
 * Checks if a permission key matches any wildcard pattern in the given set.
 *
 * Supports segment-based wildcards:
 *   "ari.*"       matches "ari.rates", "ari.rates.edit", etc.
 *   "ari.rates.*" matches "ari.rates.edit" but NOT "ari.other"
 *   "*"           matches everything
 *
 * @param {string} permission - The permission key to check (e.g. "ari.rates.edit")
 * @param {Set<string>} set - A set of permission keys, possibly containing wildcards
 * @returns {boolean}
 */
export const matchesWildcard = (permission, set) => {
  if (set.has("*")) return true;

  const parts = permission.split(".");
  for (let i = 1; i < parts.length; i++) {
    const wildcard = parts.slice(0, i).join(".") + ".*";
    if (set.has(wildcard)) return true;
  }

  return false;
};

/**
 * Default-deny permission check that mirrors backend wildcard logic.
 *
 * Evaluation order:
 *   1. Exact deny   → blocked
 *   2. Wildcard deny → blocked
 *   3. Exact allow   → permitted
 *   4. Wildcard allow → permitted
 *   5. Default        → blocked (deny)
 *
 * @param {string} permKey - Permission key to evaluate
 * @param {Set<string>} allowSet - Set of allowed permission keys
 * @param {Set<string>} denySet - Set of denied permission keys
 * @returns {boolean}
 */
export const can = (permKey, allowSet, denySet) => {
  // Deny takes priority — check exact then wildcard
  if (denySet.has(permKey)) return false;
  if (matchesWildcard(permKey, denySet)) return false;

  // Allow — check exact then wildcard
  if (allowSet.has(permKey)) return true;
  if (matchesWildcard(permKey, allowSet)) return true;

  // Default deny — if not explicitly allowed, it's blocked
  return false;
};
